import React, { useEffect, useRef, useState } from 'react';
import { useMount } from '@shared/utils/hooks/useMount';
import { useHotelBookingGuestSiteTelemetry } from '../../HotelBooking.telemetry';
import { JoyLogoLoader } from '@shared/components/JoyLogoLoader';
import { withWindow } from '@shared/utils/withWindow';
import { useHotelDetailsParams } from './useHotelDetailsParams';
import { config } from '@static/js/env.config';
import { addError } from '@shared/utils/logger';
import { RedirectWithSafeLink } from '@apps/guest/components/RedirectWithSafeLink';
import { HotelProvider } from '@graphql/generated';
import { ensureHttpsPrefix } from '@shared/utils/urlHelpers';
import { useFeatureValue } from '@shared/core/featureFlags';
import { HotelDetailsIdType, HotelDetailsQueryParams, TelHotelDetailsRedirectParams } from './HotelDetails.types';
import { formatRedirectURL } from '@shared/utils/formatting/redirectURL.formatter';
import { useHistory } from '@shared/core';
import { useHotelRoutePaths } from '../../HotelBooking.routes';
import { useQueryParamHelper } from '@shared/core/queryString';

export const HotelDetails = ({ joyPlaceId: joyPlaceIdArg, eventHandle }: { joyPlaceId?: string; eventHandle?: string }) => {
  const [safeLink, setSafeLink] = useState<string | undefined>();
  const redirectInProgress = useRef(false);
  const telemetry = useHotelBookingGuestSiteTelemetry();
  const history = useHistory();
  const hotelRoute = useHotelRoutePaths();

  const { value: accommodationsGuestSafeLink, loading: loadingAccommodationsGuestSafeLink } = useFeatureValue('accommodationsGuestSafeLinkEnabled');
  const { value: guestLinkUseRedirectService, loading: loadingGuestLinkUseRedirectService } = useFeatureValue('guestLinkUseRedirectService');
  const guestLinkUseRedirectServiceEnabled = guestLinkUseRedirectService === 'on';

  // @todo - Cleanup with ticket - https://withjoy.atlassian.net/browse/TRAV-622
  const queryParamHelper = useQueryParamHelper();
  const joyPlaceId = joyPlaceIdArg || queryParamHelper.getValueString(HotelDetailsQueryParams.joyPlaceId);

  const { loading, logErrors, ...restParams } = useHotelDetailsParams(joyPlaceId);
  const accommodationsGuestSafeLinkEnabled = accommodationsGuestSafeLink === 'on' && restParams.hasExternalLink === 'true';

  useMount(() => {
    if (joyPlaceId) {
      telemetry.hotelDetails.enter();
    } else {
      telemetry.hotelDetailsDeprecated.enter();
    }
  });

  useEffect(() => {
    if (redirectInProgress.current || loading || loadingAccommodationsGuestSafeLink || loadingGuestLinkUseRedirectService) {
      return;
    }

    try {
      logErrors();

      const { provider, ...telemetryParams } = restParams;

      let destinationName = restParams.provider ?? 'notFound';
      if (restParams.provider === HotelProvider.zh) {
        destinationName = 'hotels';
      } else if (restParams.provider === HotelProvider.nuitee) {
        destinationName = 'stays';
      }
      if (restParams.customUrl) {
        destinationName = HotelProvider.customUrl;
      }
      const telemetryPayload: TelHotelDetailsRedirectParams = { destinationName, ...telemetryParams };

      const redirectUrlPath = restParams.customUrl ?? restParams.externalPDPUrl ?? restParams.hotelWebsite;
      if (
        redirectUrlPath &&
        (restParams.customUrl || restParams.provider === HotelProvider.customUrl || restParams.provider === HotelProvider.hotelWebsiteUrl || !restParams.externalPDPUrl)
      ) {
        const hotelWebsiteUrl = new URL(ensureHttpsPrefix(redirectUrlPath));
        let hotelWebsiteWithRedirectService = redirectUrlPath;
        const redirectServicePath = `/eventredirect/`;
        if (guestLinkUseRedirectServiceEnabled && !redirectUrlPath.includes(redirectServicePath)) {
          // Every link should pass through redirect service, some accommodation places have hotel link instead of eventredirect link
          hotelWebsiteWithRedirectService = formatRedirectURL({ url: redirectUrlPath });
        }
        telemetry.partnerHotelDetailsRedirect({
          destinationUrl: hotelWebsiteUrl,
          ...telemetryPayload,
          onTelemetrySend: () => {
            if (accommodationsGuestSafeLinkEnabled) {
              setSafeLink(redirectUrlPath!);
            } else {
              withWindow(global => global.location.replace(hotelWebsiteWithRedirectService));
            }
          }
        });

        redirectInProgress.current = true;
        return;

        // Rest all provider redirect logic
      } else if (restParams.externalPDPUrl) {
        const redirectUrl = new URL(restParams.externalPDPUrl);
        if (restParams.provider === HotelProvider.zh) {
          redirectUrl.searchParams.set('campaignid', restParams.clientReference);
        } else {
          redirectUrl.searchParams.set('clientReference', restParams.clientReference);
        }
        if (restParams.currencyCode) {
          redirectUrl.searchParams.set('currency', `${restParams.currencyCode}`);
        }

        telemetry.partnerHotelDetailsRedirect({
          destinationUrl: redirectUrl,
          ...telemetryPayload,
          onTelemetrySend: () => {
            withWindow(global => global.location.replace(redirectUrl.toString()));
          }
        });

        redirectInProgress.current = true;
        return;

        // Fallback if don't have joyPlace for zh.
      } else if (restParams.sourceType === HotelDetailsIdType.hotels && joyPlaceId) {
        const zhUrl = new URL(`${config.zentrumhubWhitelableBaseUrl}/${joyPlaceId}`);
        zhUrl.searchParams.set('campaignid', restParams.clientReference);
        if (restParams.isCheckInOrCheckoutValid && restParams.checkIn && restParams.checkOut) {
          zhUrl.searchParams.set('checkin', restParams.checkIn);
          zhUrl.searchParams.set('checkout', restParams.checkOut);
        }
        if (restParams.currencyCode) {
          zhUrl.searchParams.set('currency', `${restParams.currencyCode}`);
        }
        telemetry.partnerHotelDetailsRedirect({
          ...telemetryPayload,
          destinationName: 'hotels',
          destinationUrl: zhUrl,
          onTelemetrySend: () => {
            withWindow(global => global.location.replace(zhUrl.toString()));
          }
        });

        redirectInProgress.current = true;
        return;

        // Fallback if don't have joyPlace for nuitee.
      } else if (restParams.sourceType === HotelDetailsIdType.stays && joyPlaceId) {
        const nuiteeUrl = new URL(`${config.nuiteeWhitelableBaseUrl}/${joyPlaceId}`);
        nuiteeUrl.searchParams.set('clientReference', restParams.clientReference);
        if (restParams.isCheckInOrCheckoutValid && restParams.checkIn && restParams.checkOut) {
          nuiteeUrl.searchParams.set('checkin', restParams.checkIn);
          nuiteeUrl.searchParams.set('checkout', restParams.checkOut);
        }
        if (restParams.currencyCode) {
          nuiteeUrl.searchParams.set('currency', `${restParams.currencyCode}`);
        }
        telemetry.partnerHotelDetailsRedirect({
          ...telemetryPayload,
          destinationName: 'stays',
          destinationUrl: nuiteeUrl,
          onTelemetrySend: () => {
            withWindow(global => global.location.replace(nuiteeUrl.toString()));
          }
        });

        redirectInProgress.current = true;
        return;

        // Show 404 if not find any valid redirect url
      } else {
        telemetry.partnerHotelDetailsRedirect({
          destinationUrl: new URL(withWindow(global => global.location.href, '')),
          ...telemetryPayload
        });
        if (eventHandle) {
          history.push(hotelRoute.hotelSearch.buildRelativeUrl(eventHandle, { utm_medium: 'web', utm_source: 'hotel_details', utm_campaign: 'error_not_found' }));
        } else {
          withWindow(global => global.location.replace(config.nuiteeWhitelableBaseUrl));
        }
      }

      redirectInProgress.current = true;
    } catch (error) {
      addError({
        message: 'HotelDetails: Failed to build redirect URL',
        context: 'HotelDetails',
        errorDetails: error instanceof Error ? error.message : String(error)
      });
      if (eventHandle) {
        history.push(hotelRoute.hotelSearch.buildRelativeUrl(eventHandle, { utm_medium: 'web', utm_source: 'hotel_details', utm_campaign: 'error_build_url_failed' }));
      } else {
        withWindow(global => global.location.replace(config.nuiteeWhitelableBaseUrl));
      }
      redirectInProgress.current = true;
    }
  }, [
    logErrors,
    loading,
    restParams,
    telemetry,
    guestLinkUseRedirectServiceEnabled,
    loadingAccommodationsGuestSafeLink,
    loadingGuestLinkUseRedirectService,
    accommodationsGuestSafeLinkEnabled,
    history,
    eventHandle,
    hotelRoute,
    joyPlaceId
  ]);

  return (
    <>
      {!redirectInProgress.current && <JoyLogoLoader loaderKey="hotel-details" />}
      <RedirectWithSafeLink
        href={safeLink}
        skipTelemetry
        showEmptyTabMessage
        target="_self"
        onClose={() => {
          setSafeLink(undefined);
        }}
      />
    </>
  );
};
